import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import { toast } from 'react-toastify'

const getTransactionLists = async (token) => {
  return apiGet({ url: 'transaction_lists', token })
}

export function useTransactionLists({ options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    'transaction_lists',
    () => getTransactionLists(token),
    { ...options }
  )
}

const getTransactionList = async (token, id) => {
  return apiGet({ url: `transaction_lists/${id}`, token })
}

export function useTransactionList({ id, options }) {
  const token = useContext(AuthContext)
  return useQuery(
    `transaction_lists/${id}`,
    () => getTransactionList(token, id),
    { enabled: !!id, ...options }
  )
}

export function useCreateTransactionList() {
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  return useMutation(
    (params) => {
      return apiPost({
        url: 'transaction_lists',
        token,
        objectName: 'transaction_list',
        params
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('transaction_lists')
      }
    }
  )
}

export function useUpdateTransactionList({ id }) {
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  return useMutation(
    (params) => {
      return apiPatch({
        url: `transaction_lists/${id}`,
        token,
        objectName: 'transaction_list',
        params
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('transaction_lists')
        queryClient.invalidateQueries(`transaction_lists/${id}`)
      }
    }
  )
}

export function useDestroyTransactionList({ id }) {
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  return useMutation(
    () => {
      return apiDelete({ url: `transaction_lists/${id}`, token })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('transaction_lists')
        toast.warn('List deleted', {
          autoClose: 3000
        })
      }
    }
  )
}

export function useAddToTransactionList() {
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  return useMutation(
    ({ listId, transactionId }) => {
      return apiPost({
        url: `transaction_lists/${listId}/transaction_list_items`,
        token,
        objectName: 'transaction_list_item',
        params: { transaction_id: transactionId }
      })
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('transaction_lists')
        queryClient.invalidateQueries(`transaction_lists/${variables.listId}`)
        queryClient.invalidateQueries(['transactions'])
        queryClient.invalidateQueries(`transactions/${variables.transactionId}`)
      }
    }
  )
}

export function useRemoveFromTransactionList() {
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  return useMutation(
    ({ listId, transactionId }) => {
      return apiDelete({
        url: `transaction_lists/${listId}/transaction_list_items/by_transaction?transaction_id=${transactionId}`,
        token
      })
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('transaction_lists')
        queryClient.invalidateQueries(`transaction_lists/${variables.listId}`)
        queryClient.invalidateQueries(['transactions'])
        queryClient.invalidateQueries(`transactions/${variables.transactionId}`)
      }
    }
  )
}
